import { JSXElementConstructor, memo, SVGProps, useState } from "react";
import { Flex } from "../flex";
import * as reviewsRatingCss from "./reviews-rating.module.css";

export const ReviewsRating = memo(function ReviewsRating({
  rating,
  Icon,
  iconSize = 24,
  iconColors = { filled: "#FEC84B", empty: "#E5E5E5" },
  starPadding,
  starOutlineEnabled,
  starOutlineColor,
  starOutlineWidth,
  editable = false,
  onRatingChange,
}: {
  rating: number | undefined;
  Icon: JSXElementConstructor<SVGProps<SVGSVGElement>>;
  iconColors?: { filled: string; empty: string };
  iconSize?: number;
  starPadding?: number;
  starOutlineEnabled?: boolean;
  starOutlineColor?: string;
  starOutlineWidth?: number;
  editable?: boolean;
  onRatingChange?: (rating: number) => void;
}) {
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  function handleSetSelectedRating(rating: number) {
    setSelectedRating(rating);
    onRatingChange?.(rating);
  }

  const handleMouseEnter = (index: number) => {
    if (editable) {
      setHoveredRating(index + 1);
    }
  };

  const handleMouseLeave = () => {
    if (editable) {
      setHoveredRating(null);
    }
  };

  const handleClick = (index: number) => {
    if (editable) {
      handleSetSelectedRating(index + 1);
    }
  };

  const outlineStyles = starOutlineEnabled
    ? { stroke: starOutlineColor || "#000", strokeWidth: starOutlineWidth || 1 }
    : {};

  const currentRating = hoveredRating ?? selectedRating ?? rating ?? 0;

  return (
    <Flex dir="row" style={starPadding ? { gap: `${starPadding}px` } : {}}>
      {[...Array(5)].map((_, index) => {
        const fillPercentage =
          Math.min(Math.max(currentRating - index, 0), 1) * 100;

        return (
          <div
            className={reviewsRatingCss.starContainer}
            key={index}
            onClick={() => handleClick(index)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            style={{
              cursor: editable ? "pointer" : "default",
              position: "relative",
              width: iconSize,
              height: iconSize,
              display: "inline-block",
            }}
          >
            <Icon
              className={reviewsRatingCss.icon}
              style={{
                color: iconColors.empty,
                width: iconSize,
                height: iconSize,
                position: "absolute",
                top: 0,
                left: 0,
                ...outlineStyles,
              }}
            />
            {fillPercentage > 0 && (
              <div
                style={{
                  position: "absolute",
                  overflow: "hidden",
                  width: `${fillPercentage}%`,
                  height: "100%",
                  top: 0,
                  left: 0,
                }}
              >
                <Icon
                  className={reviewsRatingCss.icon}
                  style={{
                    color: iconColors.filled,
                    width: iconSize,
                    height: iconSize,
                    ...outlineStyles,
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </Flex>
  );
});
